// Rule keys mirror frontend-reactor's sc-validator so messages stay aligned.
export const PASSWORD_RULES = [
  "hasPasswordValueOrEmptyAllowed",
  "isAboveMinLength",
  "isBelowMaxLength",
  "hasLowerCase",
  "hasUpperCase",
  "hasNumberOrSpecialChar",
] as const;
export type PasswordRule = (typeof PASSWORD_RULES)[number];

export const PASSWORD_MIN_LENGTH = 8;
export const PASSWORD_MAX_LENGTH = 50;

const PASSWORD_RULE_ERROR_MSG: Record<PasswordRule, string> = {
  hasPasswordValueOrEmptyAllowed: "Password is required",
  isAboveMinLength: `The password must have a minimum of ${PASSWORD_MIN_LENGTH} characters.`,
  isBelowMaxLength: `The password must be at most ${PASSWORD_MAX_LENGTH} characters long.`,
  hasLowerCase: "The password must include at least one lowercase letter.",
  hasUpperCase: "The password must include at least one uppercase letter.",
  hasNumberOrSpecialChar:
    "The password must include at least one number or special character.",
};

// OWASP password special characters
const SPECIAL_CHARS_REGEXP = new RegExp(
  [
    " ",
    "!",
    '"',
    "#",
    "\\$",
    "%",
    "&",
    "'",
    "\\(",
    "\\)",
    "\\*",
    "\\+",
    ",",
    "-",
    "\\.",
    "/",
    ":",
    ";",
    "<",
    "=",
    ">",
    "\\?",
    "@",
    "\\[",
    "\\\\",
    "\\]",
    "\\^",
    "_",
    "`",
    "{",
    "\\|",
    "}",
    "~",
  ].join("|")
);

export function isValidPassword(
  password: string,
  allowEmptyPw = false
): {
  isValid: boolean;
  // All need to be true for password to be valid
  validFields: Record<PasswordRule, boolean>;
} {
  let isValid = allowEmptyPw;
  const validFields: Record<PasswordRule, boolean> = {
    hasPasswordValueOrEmptyAllowed: allowEmptyPw,
    isAboveMinLength: false,
    isBelowMaxLength: true,
    hasLowerCase: false,
    hasUpperCase: false,
    hasNumberOrSpecialChar: false,
  };

  if (!allowEmptyPw && password.length === 0) {
    validFields.hasPasswordValueOrEmptyAllowed = false;
  }

  if (password.length > 0) {
    validFields.hasPasswordValueOrEmptyAllowed = true;

    if (password.length >= PASSWORD_MIN_LENGTH) {
      validFields.isAboveMinLength = true;
    }

    if (password.length > PASSWORD_MAX_LENGTH) {
      validFields.isBelowMaxLength = false;
    }

    if (/[a-z]/.test(password)) {
      validFields.hasLowerCase = true;
    }

    if (/[A-Z]/.test(password)) {
      validFields.hasUpperCase = true;
    }

    if (/\d/.test(password) || SPECIAL_CHARS_REGEXP.test(password)) {
      validFields.hasNumberOrSpecialChar = true;
    }

    isValid = Object.values(validFields).every((field) => field === true);
  }

  return { isValid, validFields };
}

export class Validator {
  public validateEmail = (email: string) => {
    if (email.length === 0) {
      return "Email is required";
    } else if (email.length < 3) {
      return "Email must be at least 3 characters long";
    } else if (email.length > 200) {
      return "Email must be at most 200 characters long";
    } else if (!isEmail2(email)) {
      return "Email is invalid";
    }
    return;
  };

  public validateUsername = (username: string) => {
    if (username.length === 0) {
      return "Username is required";
    } else if (username.length < 3) {
      return "Username must be at least 3 characters long";
    } else if (username.length > 200) {
      return "Username must be at most 200 characters long";
    } else if (username.includes("@")) {
      return "Username cannot contain @. To convert this user to an email account, enter a valid email instead.";
    } else if (/\s/.test(username)) {
      return "Username must not contain whitespace";
    }
    return;
  };

  public validatePassword = (
    password: string,
    allowEmptyPw = false
  ): string[] => {
    const sanitized = password.trim();
    const { isValid, validFields } = isValidPassword(sanitized, allowEmptyPw);

    if (isValid) {
      return [];
    }

    return PASSWORD_RULES.filter((rule) => !validFields[rule]).map(
      (rule) => PASSWORD_RULE_ERROR_MSG[rule]
    );
  };
}

const EMAIL_VALIDATOR2 =
  /^[a-z0-9!#$%&'*+\/=?^_{|}~-]+(?:\.[a-z0-9!#$%&'*+\/=?^_{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?/i;

export function isEmail2(email: string): boolean {
  return EMAIL_VALIDATOR2.test(email);
}
